import * as Cesium from 'cesium';
import 'cesium/Build/Cesium/Widgets/widgets.css';

/**
 * Manages the Cesium 3D viewer and scene configuration
 */
export class CesiumManager {
  constructor(containerId, ionToken) {
    this.hasIonToken = Boolean(ionToken && ionToken.trim().length > 0);

    // Set Cesium Ion token
    if (this.hasIonToken) {
      Cesium.Ion.defaultAccessToken = ionToken;
    }
    
    // Create the viewer with basic settings first
    this.viewer = new Cesium.Viewer(containerId, {
      // Scene settings
      scene3DOnly: true,
      shouldAnimate: true,
      imageryProvider: this.hasIonToken ? undefined : new Cesium.UrlTemplateImageryProvider({
        url: 'https://tile.openstreetmap.org/{z}/{x}/{y}.png',
        credit: '© OpenStreetMap'
      }),
      
      // UI elements (we'll hide most of these)
      animation: false,
      timeline: false,
      baseLayerPicker: false,
      geocoder: false,
      homeButton: false,
      sceneModePicker: false,
      navigationHelpButton: false,
      fullscreenButton: false,
      vrButton: false,
      selectionIndicator: false, // Enable selection indicator (we'll customize it)
      
      // Performance
      requestRenderMode: false, // Continuous rendering for smooth updates
      maximumRenderTimeChange: Infinity
    });
    
    // Set terrain provider asynchronously
    this.initTerrain();
    
    // Configure scene for best visuals
    this.configureScene();
    
    // Configure camera controls
    this.configureCameraControls();
    
    // Customize selection indicator color (blue instead of green)
    if (this.viewer.selectionIndicator) {
      this.viewer.selectionIndicator.viewModel.selectionIndicatorElement.style.borderColor = '#3b82f6';
    }
    
    // Set initial camera position (centered over Europe)
    this.viewer.camera.setView({
      destination: Cesium.Cartesian3.fromDegrees(4.0, 47.0, 1500000), // lon, lat, height
      orientation: {
        heading: Cesium.Math.toRadians(0),
        pitch: Cesium.Math.toRadians(-45),
        roll: 0.0
      }
    });
  }
  
  /**
   * Initialize terrain provider
   */
  async initTerrain() {
    try {
      if (!this.hasIonToken) {
        this.viewer.scene.terrainProvider = new Cesium.EllipsoidTerrainProvider();
        this.useFallbackImagery();
        console.warn('⚠️ No Cesium Ion token provided; using ellipsoid terrain and OSM imagery.');
        return;
      }

      this.viewer.scene.terrainProvider = await Cesium.CesiumTerrainProvider.fromIonAssetId(1, {
        requestWaterMask: true,
        requestVertexNormals: true
      });
      console.log('✅ Terrain provider initialized');
    } catch (error) {
      console.warn('⚠️ Failed to load terrain, using default ellipsoid:', error);
       this.viewer.scene.terrainProvider = new Cesium.EllipsoidTerrainProvider();
       this.useFallbackImagery();
    }
  }

  /**
   * Use token-free OSM imagery as a fallback
   */
  useFallbackImagery() {
    try {
      const osm = new Cesium.UrlTemplateImageryProvider({
        url: 'https://tile.openstreetmap.org/{z}/{x}/{y}.png',
        credit: '© OpenStreetMap'
      });
      this.viewer.imageryLayers.removeAll();
      this.viewer.imageryLayers.addImageryProvider(osm);
      console.log('✅ Using OpenStreetMap imagery (no Ion token)');
    } catch (err) {
      console.warn('⚠️ Unable to apply fallback imagery:', err);
    }
  }
  
  /**
   * Configure camera controls
   */
  configureCameraControls() {
    const scene = this.viewer.scene;
    const controller = scene.screenSpaceCameraController;

    // Enable the controls we want
    controller.enableRotate = true;    // for orbiting
    controller.enableTranslate = true; // middle-drag pan
    controller.enableZoom = true;      // wheel zoom
    controller.enableTilt = false;     // keep disabled to avoid zoomy tilt
    controller.enableLook = false;     // IMPORTANT: do not use look for orbiting

    // Gestures mapping
    // Left-drag: rotate/orbit
    // Right-drag: rotate/orbit (we'll set/clear a pivot on right down/up)
    controller.rotateEventTypes = [
      Cesium.CameraEventType.LEFT_DRAG,
      Cesium.CameraEventType.RIGHT_DRAG
    ];

    // Pan on middle-drag only
    controller.translateEventTypes = [
      Cesium.CameraEventType.MIDDLE_DRAG
    ];

    // No tilt or look bindings
    controller.tiltEventTypes = [];
    controller.lookEventTypes = [];

    // Inertia tuning
    controller.inertiaSpin = 0.9;
    controller.inertiaTranslate = 0.9;
    controller.inertiaZoom = 0.8;

    // ----- Orbit around center of view (no jump on right-click) -----
    const handler = new Cesium.ScreenSpaceEventHandler(this.viewer.canvas);
    let pivotActive = false;

    // On right button down: set pivot at current view center (not at mouse position)
    handler.setInputAction(() => {
      const scene = this.viewer.scene;
      const camera = scene.camera;
      const globe = scene.globe;

      // Get center of screen
      const centerX = scene.canvas.clientWidth / 2;
      const centerY = scene.canvas.clientHeight / 2;
      const centerPosition = new Cesium.Cartesian2(centerX, centerY);

      let target = undefined;

      // Try picking at screen center
      const ray = camera.getPickRay(centerPosition);
      if (ray) {
        target = globe.pick(ray, scene);
      }

      // Fallback: use a point along camera direction
      if (!target) {
        // Get current camera height and use that as approximate distance to target
        const cartographic = Cesium.Cartographic.fromCartesian(camera.position);
        const height = cartographic.height;
        const direction = camera.direction;
        target = Cesium.Cartesian3.add(
          camera.position,
          Cesium.Cartesian3.multiplyByScalar(direction, height * 0.8, new Cesium.Cartesian3()),
          new Cesium.Cartesian3()
        );
      }

      if (target) {
        const range = Cesium.Cartesian3.distance(camera.position, target);
        // Use lookAt with current heading/pitch so view doesn't change
        camera.lookAt(
          target,
          new Cesium.HeadingPitchRange(camera.heading, camera.pitch, range)
        );
        pivotActive = true;
      }
    }, Cesium.ScreenSpaceEventType.RIGHT_DOWN);

    // On right button up: clear the pivot so normal navigation resumes
    handler.setInputAction(() => {
      if (pivotActive) {
        this.viewer.camera.lookAtTransform(Cesium.Matrix4.IDENTITY);
        pivotActive = false;
      }
    }, Cesium.ScreenSpaceEventType.RIGHT_UP);
  }
  
  /**
   * Configure scene for optimal visuals and performance
   */
  configureScene() {
    const scene = this.viewer.scene;
    
    // FORCE DAYTIME EVERYWHERE - Override sun position
    // Position sun at high noon (straight up from equator)
    const sunPosition = new Cesium.Cartesian3.fromDegrees(0, 0, 150000000);
    scene.light = new Cesium.SunLight({
      intensity: 2.0 // Brighter sun
    });
    
    // Disable dynamic lighting so it stays bright
    scene.globe.enableLighting = false; // Keep it bright everywhere
    scene.globe.dynamicAtmosphereLighting = false;
    scene.globe.showGroundAtmosphere = true;
    
    // Enhanced atmosphere settings for flight sim look
    scene.skyAtmosphere.show = true;
    scene.skyAtmosphere.brightnessShift = 0.3; // Bright but natural
    scene.skyAtmosphere.hueShift = 0.0; // Natural blue - no shift
    scene.skyAtmosphere.saturationShift = 0.0; // Natural saturation
    
    // Professional flight sim atmosphere with visible haze at all altitudes
    scene.fog.enabled = true;
    scene.fog.density = 0.00215; // Subtle atmospheric haze
    scene.fog.minimumBrightness = 0.4; // Keep it bright even with fog
    scene.fog.screenSpaceErrorFactor = 2.0; // More visible at distance
    
    // Anti-aliasing
    if (scene.postProcessStages.fxaa) {
      scene.postProcessStages.fxaa.enabled = true;
    }
    
    // MSAA if available
    if (scene.msaaSamples !== undefined) {
      scene.msaaSamples = 4;
    }
    
    // Disable shadows for better performance and brightness
    scene.shadowMap.enabled = false;
    
    // High-quality rendering
    scene.highDynamicRange = true;
    scene.requestRenderMode = false; // Always render for smooth updates
    
    // Sun and sky
    scene.sun.show = true;
    scene.moon.show = false; // Hide moon for daytime look
    scene.skyBox.show = true;
    
    // Proper Earth colors
    scene.globe.baseColor = Cesium.Color.fromCssColorString('#2C3E50'); // Dark blue-grey for ocean
    scene.backgroundColor = Cesium.Color.fromCssColorString('#87CEEB'); // Proper sky blue (Sky Blue)
    
    // Enhanced atmosphere for flight sim look - visible at ALL altitudes
    scene.globe.atmosphereBrightnessShift = 0.2; // Subtle brightness boost
    scene.globe.atmosphereHueShift = 0.0; // Keep natural blue
    scene.globe.atmosphereSaturationShift = 0.0; // Natural colors
    scene.globe.atmosphereLightIntensity = 10.0; // Moderate intensity for realism
    
    // Ground atmosphere always visible
    scene.globe.showGroundAtmosphere = true;
    scene.globe.translucency.enabled = false; // Better performance
    
    // Enable depth testing against terrain for proper occlusion
    scene.globe.depthTestAgainstTerrain = true;
    
    // Color correction for realistic look
    if (scene.postProcessStages) {
      scene.brightness = 1.2; // Slightly brighter overall
      scene.gamma = 1.0;
    }
  }
  
  /**
   * Set the camera view to a specific position
   */
  setCameraView(lon, lat, height, heading = 0, pitch = -45, roll = 0) {
    this.viewer.camera.setView({
      destination: Cesium.Cartesian3.fromDegrees(lon, lat, height),
      orientation: {
        heading: Cesium.Math.toRadians(heading),
        pitch: Cesium.Math.toRadians(pitch),
        roll: Cesium.Math.toRadians(roll)
      }
    });
  }
  
  /**
   * Get current camera position
   */
  getCameraPosition() {
    const camera = this.viewer.camera;
    const cartographic = Cesium.Cartographic.fromCartesian(camera.position);
    
    return {
      lon: Cesium.Math.toDegrees(cartographic.longitude),
      lat: Cesium.Math.toDegrees(cartographic.latitude),
      height: cartographic.height,
      heading: Cesium.Math.toDegrees(camera.heading),
      pitch: Cesium.Math.toDegrees(camera.pitch),
      roll: Cesium.Math.toDegrees(camera.roll)
    };
  }
  
  /**
   * Fly to a specific location
   */
  flyTo(lon, lat, height, duration = 2.0) {
    this.viewer.camera.flyTo({
      destination: Cesium.Cartesian3.fromDegrees(lon, lat, height),
      duration: duration,
      orientation: {
        heading: Cesium.Math.toRadians(0),
        pitch: Cesium.Math.toRadians(-45),
        roll: 0.0
      }
    });
  }
  
  /**
   * Enable/disable shadows
   */
  setShadowsEnabled(enabled) {
    this.viewer.scene.shadowMap.enabled = enabled;
  }
  
  /**
   * Enable/disable terrain
   */
  async setTerrainEnabled(enabled) {
    if (enabled) {
      this.viewer.scene.terrainProvider = await Cesium.CesiumTerrainProvider.fromIonAssetId(1, {
        requestWaterMask: true,
        requestVertexNormals: true
      });
    } else {
      this.viewer.scene.terrainProvider = new Cesium.EllipsoidTerrainProvider();
    }
  }
  
  /**
   * Pause rendering (e.g. when switching to 2D mode).
   * Switches to request-render mode so the GPU idles.
   */
  pauseRendering() {
    if (this.viewer && this.viewer.scene) {
      this.viewer.scene.requestRenderMode = true;
      this.viewer.scene.maximumRenderTimeChange = Infinity;
    }
  }

  /**
   * Resume continuous rendering (e.g. when switching back to 3D mode).
   */
  resumeRendering() {
    if (this.viewer && this.viewer.scene) {
      this.viewer.scene.requestRenderMode = false;
      this.viewer.scene.requestRender();
    }
  }

  /**
   * Destroy the viewer and clean up resources
   */
  destroy() {
    if (this.viewer) {
      this.viewer.destroy();
      this.viewer = null;
    }
  }
}
